import * as React from 'react';
import { BrandName, BrandTile } from '@/components/ui/brand-mark';
import { CONFIG } from '@/config-globals';
import { cn } from '@/lib/utils';
import { Field } from './field';
import { PasswordField } from './profile';

/**
 * Tela de login — rota `/sign-in`, fora do layout com sidebar. Ver prints/08-login.png e prints/09-login-mobile.png.
 *
 * Desktop (lg+): duas colunas. Esquerda `flex-[1.15]` = painel da marca;
 * direita `flex-1` = formulário em bg-sunken, centralizado, max-w 400px.
 * Mobile (< lg): o painel vira uma faixa de 220px no topo (logo + "ACESSO RESTRITO") que se funde
 * no bg-sunken, e o formulário vem logo abaixo com gutter de 20px.
 *
 * Campos: os mesmos `Field` / `PasswordField` do admin (rótulo dentro, raio 14, trilho oliva no foco).
 * Botão: pílula areia, único da tela. Sem seletor de tema, sem Sim/Não para "lembrar-me".
 */

const BrandMark: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <div className="relative flex items-center gap-3">
    <BrandTile className={compact ? 'size-9' : 'size-11'} />
    <span className="flex flex-col gap-[3px]">
      <BrandName className={compact ? 'text-base' : 'text-[17px]'} />
      <span className="col-label text-faint">{CONFIG.APP_AUTHOR}</span>
    </span>
  </div>
);

const Kicker: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="relative inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[.14em] text-olive-ink">
    <span className="h-px w-6 bg-olive-icon" />
    {children}
  </span>
);

/** Painel da marca (desktop). */
export const LoginBrandPanel: React.FC<{ year?: number }> = ({
  year = new Date().getFullYear(),
}) => (
  <section className="relative hidden min-h-svh flex-[1.15] flex-col justify-between overflow-hidden border-r border-border px-14 py-12 lg:flex">
    <BrandMark />

    <div className="relative flex max-w-[520px] flex-col gap-5">
      <Kicker>Acesso restrito</Kicker>
      <h1 className="font-display m-0 text-[clamp(40px,3.6vw,52px)] font-extrabold leading-[1.04] tracking-[-.03em] text-balance text-white">
        Suas cifras e repertórios em um só lugar.
      </h1>
      <p className="m-0 max-w-[440px] text-base leading-relaxed text-pretty text-muted-foreground">
        Cifras, repertórios e artistas do ministério de música, com transposição e modo palco.
      </p>
    </div>

    <div className="relative flex gap-7 text-xs text-muted-foreground">
      <span>© {year} Chordfy</span>
      <span>by Felipe Augusto</span>
    </div>
  </section>
);

/** Faixa da marca (mobile). */
export const LoginBrandStrip: React.FC = () => (
  <section className="relative flex h-[220px] flex-col justify-between overflow-hidden px-5 pb-[26px] pt-[22px] lg:hidden">
    <BrandMark compact />
    <Kicker>Acesso restrito</Kicker>
  </section>
);

const Checkbox: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
}> = ({ checked, onChange, children }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className="flex items-center gap-2.5 text-[13px] text-muted-foreground"
  >
    <span
      className={cn(
        'grid size-[18px] place-items-center rounded-[6px] border transition-colors duration-100',
        checked ? 'border-sand bg-sand text-on-sand' : 'border-sand/30 bg-transparent',
      )}
    >
      {checked && (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m5 12 5 5 9-10" />
        </svg>
      )}
    </span>
    {children}
  </button>
);

export type LoginValues = { email: string; password: string; remember: boolean };

export const LoginForm: React.FC<{
  onSubmit: (v: LoginValues) => void;
  /** Mensagem do backend (credencial inválida → "E-mail ou senha incorretos. Verifique e tente novamente."). */
  error?: string;
  /** Chamado quando o usuário volta a digitar — o pai limpa o erro do backend. */
  onClearError?: () => void;
  loading?: boolean;
  /** Sem rota de recuperação, o link não é exibido. */
  forgotHref?: string;
  alertIcon: React.ReactNode;
  arrowIcon: React.ReactNode;
  shieldIcon: React.ReactNode;
}> = ({
  onSubmit,
  error,
  onClearError,
  loading,
  forgotHref,
  alertIcon,
  arrowIcon,
  shieldIcon,
}) => {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [remember, setRemember] = React.useState(true);
  const [localError, setLocalError] = React.useState('');
  const shownError = localError || error;

  const clearErrors = () => {
    setLocalError('');
    onClearError?.();
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password)
      return setLocalError('Informe e-mail e senha para continuar.');
    setLocalError('');
    onSubmit({ email, password, remember });
  };

  return (
    <section className="flex flex-1 basis-[400px] items-center lg:basis-0 justify-center bg-sunken px-5 pb-10 pt-2 lg:py-12">
      <form
        onSubmit={submit}
        noValidate
        className="flex w-full max-w-[400px] flex-col gap-7"
      >
        <div className="flex flex-col gap-2">
          <h2 className="font-display m-0 text-[clamp(26px,3vw,30px)] font-extrabold tracking-[-.02em] text-white">
            Entrar
          </h2>
          <p className="m-0 text-sm text-muted-foreground">
            Use suas credenciais de administrador.
          </p>
        </div>

        {shownError && (
          <div
            role="alert"
            className="flex items-start gap-2.5 rounded-[14px] border border-destructive/40 bg-destructive/10 px-3.5 py-3 text-[13px] leading-snug text-destructive"
          >
            <span className="mt-px shrink-0">{alertIcon}</span>
            <span>{shownError}</span>
          </div>
        )}

        <div className="flex flex-col gap-4">
          <Field
            label="E-mail"
            type="email"
            autoComplete="username"
            placeholder="voce@chordfy.alistese.com.br"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearErrors();
            }}
            fieldClassName={shownError ? 'border-destructive/55' : undefined}
          />
          <PasswordField
            label="Senha"
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              clearErrors();
            }}
            invalid={!!shownError}
          />
          <div className="flex flex-wrap items-center justify-between gap-3 px-1">
            <Checkbox checked={remember} onChange={setRemember}>
              Manter conectado
            </Checkbox>
            {forgotHref && (
              <a
                href={forgotHref}
                className="text-[13px] font-semibold text-sand hover:text-sand/80"
              >
                Esqueci a senha
              </a>
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="flex h-12 items-center justify-center gap-2 rounded-full bg-sand text-sm font-bold text-on-sand transition-[background-color,transform] duration-150 hover:bg-sand/90 active:scale-[.98] disabled:opacity-70"
        >
          {loading ? (
            <>
              <span className="size-4 animate-spin rounded-full border-2 border-on-sand/25 border-t-on-sand" />
              Verificando…
            </>
          ) : (
            <>
              Entrar no painel
              {arrowIcon}
            </>
          )}
        </button>

        <div className="flex items-center gap-2.5 border-t border-border pt-5 text-xs leading-snug text-faint">
          <span className="shrink-0 text-muted-foreground">{shieldIcon}</span>
          Conexão protegida. Acesso exclusivo à equipe organizadora.
        </div>
      </form>
    </section>
  );
};

/** Página completa. Monte em `/sign-in` sem sidebar/topbar. */
export const LoginScreen: React.FC<
  React.ComponentProps<typeof LoginForm>
> = (form) => (
  <div className="flex min-h-svh flex-col bg-background text-foreground lg:flex-row">
    <LoginBrandPanel />
    <LoginBrandStrip />
    <LoginForm {...form} />
  </div>
);
